import { useState } from 'react';
import SignaturePad from '../../../components/SignaturePad';
import styled from 'styled-components';
import TextInfoBox from '../../../components/TextInfoBox';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { apiGet, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import { trimObject } from '../../../utils/trimObject';
import { useTranslation } from 'react-i18next';
import { logPost } from '../../../services/log';

const Root = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100% - 40px);
  padding-bottom: 10rem;

  .sign_canvas {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
  }

  @media screen and (max-width: 1024px) {
    & {
      align-items: flex-start;
      padding-bottom: 0;
      overflow-y: auto;

      .sign_canvas {
        gap: 1rem;
      }
    }
  }
`;

const SignForm = () => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const [dataUrl, setDataUrl] = useState<string>(''); // 이미지 BASE64 URL
  const [isSigned, setSigned] = useState<boolean>(false); // 서명 상태
  const [isLoading, setIsLoading] = useState<boolean>(false); // 서명 데이터 불러오기까지의 로딩 상태

  useQuery(['/company/sign', userInfo.hCd], () => getSign(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
    onSuccess: (data) => {
      const newDate = new Date();
      const getTime = newDate.getTime(); // 캐시 방지용

      if (data !== '') {
        const newUrl = `${data}?date=${getTime}`;
        ConvertBase64Img(newUrl)
          .then((res) => {
            setDataUrl(String(res));
            setSigned(true);
          })
          .catch((err) => console.error(err));
      } else {
        setIsLoading(false);
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const getSign = async () => {
    setIsLoading(true);
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/company/sign', req });
    return res.data.data;
  };

  // Base64 이미지 변환
  const ConvertBase64Img = (url: string) =>
    fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );

  // 개인서명 저장
  const onClickSave = async () => {
    if (dataUrl === '') {
      toast.warn(t('서명을 해주세요'));
      return false;
    }
    const trimData = {
      cCd: userInfo.hCd,
      signBase64: dataUrl.slice(dataUrl.indexOf(',') + 1),
    };
    const req = trimObject(trimData);
    const res = await apiPost({ path: '/company/sign', req });

    if (res.status === 200) {
      toast.success(t('저장하였습니다'));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '기타 관리 > 개인 서명 관리',
        action: '저장',
        etc: `개인 서명 저장`,
      });
      return false;
    }

    return false;
  };

  return (
    <Root>
      <div className='sign_canvas'>
        <TextInfoBox
          description='1. 서명은 결재 / 근태계 작성 시 개인 서명 용도로 사용됩니다<br />2. PC 사용자일 경우 마우스 왼쪽 버튼을 누른 상태에서 서명란 안에서<br />서명을 시작하시고 서명이 끝났을 때 누르고 있던 마우스 왼쪽 버튼을 해제하시면 됩니다.'
          color='orange'
          type='fill'
        />
        <SignaturePad isSigned={isSigned} setIsSigned={setSigned} dataURL={dataUrl} setDataURL={setDataUrl} onClickSave={onClickSave} isLoading={isLoading} setIsLoading={setIsLoading} />
      </div>
    </Root>
  );
};

export default SignForm;
